/**
 * FNV-1a 64-bit over a canonical serialization (SPEC §3.8).
 * Keys are sorted; keys starting with "_" (caches, per-tick events) are skipped.
 * 64-bit arithmetic is done with four 16-bit limbs.
 */

type Limbs = [number, number, number, number];

const fnvMul = (h: Limbs): void => {
  // prime = 0x00000100_000001b3 → limbs [0x01b3, 0x0000, 0x0100, 0x0000]
  const h0 = h[0];
  const h1 = h[1];
  const h2 = h[2];
  const h3 = h[3];
  let r0 = h0 * 0x01b3;
  let r1 = h1 * 0x01b3;
  let r2 = h2 * 0x01b3 + h0 * 0x0100;
  let r3 = h3 * 0x01b3 + h1 * 0x0100;
  r1 += r0 >>> 16;
  r0 &= 0xffff;
  r2 += Math.trunc(r1 / 65536);
  r1 &= 0xffff;
  r3 += Math.trunc(r2 / 65536);
  r2 &= 0xffff;
  r3 &= 0xffff;
  h[0] = r0;
  h[1] = r1;
  h[2] = r2;
  h[3] = r3;
};

const feedByte = (h: Limbs, b: number): void => {
  h[0] = (h[0] ^ (b & 0xff)) & 0xffff;
  fnvMul(h);
};

const feedString = (h: Limbs, s: string): void => {
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    feedByte(h, c & 0xff);
    if (c > 0xff) feedByte(h, c >>> 8);
  }
};

const walk = (h: Limbs, v: unknown): void => {
  if (v === null || v === undefined) {
    feedString(h, 'n');
    return;
  }
  switch (typeof v) {
    case 'number':
      feedString(h, `#${v};`);
      return;
    case 'boolean':
      feedString(h, v ? 't' : 'f');
      return;
    case 'string':
      feedString(h, `"${v.length}:`);
      feedString(h, v);
      return;
    case 'object': {
      if (Array.isArray(v)) {
        feedString(h, `[${v.length}`);
        for (const x of v) walk(h, x);
        feedString(h, ']');
        return;
      }
      const o = v as Record<string, unknown>;
      const keys = Object.keys(o)
        .filter((k) => k.charCodeAt(0) !== 95 && o[k] !== undefined)
        .sort();
      feedString(h, '{');
      for (const k of keys) {
        feedString(h, k);
        feedString(h, ':');
        walk(h, o[k]);
      }
      feedString(h, '}');
      return;
    }
    default:
      throw new Error(`hashState: unsupported value of type ${typeof v}`);
  }
};

const hex4 = (n: number): string => {
  const s = n.toString(16);
  return '0000'.slice(s.length) + s;
};

/** 64-bit FNV-1a hash of any plain JSON-like value, as 16 hex chars. */
export const hashState = (state: unknown): string => {
  const h: Limbs = [0x2325, 0x8422, 0x9ce4, 0xcbf2];
  walk(h, state);
  return hex4(h[3]) + hex4(h[2]) + hex4(h[1]) + hex4(h[0]);
};

/** FNV-1a 64 of a raw string (used by tests to check against known vectors). */
export const fnv1a64 = (s: string): string => {
  const h: Limbs = [0x2325, 0x8422, 0x9ce4, 0xcbf2];
  for (let i = 0; i < s.length; i++) feedByte(h, s.charCodeAt(i));
  return hex4(h[3]) + hex4(h[2]) + hex4(h[1]) + hex4(h[0]);
};

/** Canonical deep copy that drops "_" keys (used for snapshots). */
export const snapshot = <T>(state: T): T =>
  JSON.parse(JSON.stringify(state, (k, v: unknown) => (k.charCodeAt(0) === 95 ? undefined : v))) as T;
